import { Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, createTheme, makeStyles } from "@material-ui/core"
import React, { useEffect, useState } from "react"
import { useIntl } from "react-intl"

/**
 * label: string - Label for table head. Translates from common with 'common.' as prefix.
 * 
 * prop: string - Row property for column.
 * 
 * type?: 'number' | 'date'  - Type for column. Defaults to string.
 *  
 * hideOnXS?: Hide column in mobile view.
 * 
 */
export interface Column {
    label: string
    prop: string
    type?: 'number' | 'date'
    hideOnXS?: boolean
    _direction?: 'asc' | 'desc'
}

const defaultTheme = createTheme()

const useStyles = makeStyles({
    hideOnXS: { [defaultTheme.breakpoints.down('xs')]: { display: 'none !important' } },
    table: { display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '100%' },
    head: { display: 'block', [defaultTheme.breakpoints.down('xs')]: { overflowY: 'hidden' } },
    headCellText: { color: '#000000' },
    headRow: { display: 'flex', width: '100%' },
    cellAlignLeft: { display: 'flex', flex: '1 0', overflow: 'hidden', alignItems: 'baseline', justifyContent: 'flex-start' },
    cellAlignRight: { display: 'flex', flex: '1 0', overflow: 'hidden', alignItems: 'baseline', justifyContent: 'flex-end' },
    body: { flex: '1', overflow: 'overlay', display: 'block' },
    bodyRow: { width: '100%', display: 'flex', height: '5rem' },
    bodyClickable: { '&:hover': { cursor: 'pointer' } },
    bodyCellText: { fontWeight: 'initial', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', paddingTop: '5px', color: '#000000' },
    pagination: { color: '#666666' },
    rightAlignSortLabel: { '& svg': { marginRight: '0' } }
})

interface CustomerPortalTableProps {
    columns: Column[]
    rows: any[]
    onRowClick?: (row: any) => void
    pageIndex?: number
    totalNumberOfPages?: number
    numberOfRows?: number
    onChangePage?: (event: unknown, pageIndex: number) => void
    onChangeNumberOfRows?: (event: React.ChangeEvent<HTMLInputElement>) => void
    canClickRow?: (row: any) => boolean
}

const CustomerPortalTable = ({
    columns,
    rows,
    onRowClick,
    pageIndex,
    totalNumberOfPages,
    numberOfRows,
    onChangePage,
    onChangeNumberOfRows,
    canClickRow = () => true
}: CustomerPortalTableProps) => {
    const styles = useStyles();
    const intl = useIntl();
    const [tableRows, setTableRows] = useState<unknown[]>();
    const [tableColumns, setTableColumns] = useState<Column[]>();
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);

    useEffect(() => {
        setTableRows(() => rows);
        setTableColumns(() => columns);
    }, [rows, columns]);

    useEffect(() => {
        if (numberOfRows) {
            setRowsPerPage(() => numberOfRows);
        }
    }, [pageIndex, numberOfRows]);

    useEffect(() => {}, []);

    const sortRows = (prop: string): void => {
        const column = columns.find((x) => x.prop === prop);
        const direction = column._direction === "asc" ? "desc" : "asc";
        setColumnDirection(column, direction);
        setSortedRows(direction, prop);
    };

    const setColumnDirection = (column: Column, direction: "asc" | "desc"): void => {
        const index = tableColumns.indexOf(column);
        tableColumns[index]._direction = direction;
        setTableColumns([...tableColumns]);
    };

    const setSortedRows = (direction: "asc" | "desc", prop: string): void => {
        const sortedRows = tableRows.sort(getCompareFn(direction, prop));
        setTableRows(sortedRows);
    };

    const getCompareFn = (direction: "asc" | "desc", prop: string): ((a: any, b: any) => number) => {
        if (isStringSort(prop)) {
            if (direction === "asc") {
                return (a: any, b: any) => (b[prop].toLowerCase() > a[prop].toLowerCase() ? 1 : -1);
            } else {
                return (a: any, b: any) => (a[prop].toLowerCase() > b[prop].toLowerCase() ? 1 : -1);
            }
        } else {
            if (direction === "asc") {
                return (a: any, b: any) => b[prop] - a[prop];
            } else {
                return (a: any, b: any) => a[prop] - b[prop];
            }
        }
    };

    const isStringSort = (prop: string): boolean => {
        return !tableColumns.find((x) => x.prop === prop)?.type;
    };

    const handleChangePage = (event: unknown, newPage: number): void => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const handleOnRowClick = (row: any): void => {
        onRowClick(row);
    };

    const getCellClassName = (column: Column): string => {
        return column.hideOnXS
            ? `${styles.hideOnXS} ${column.type ? styles.cellAlignRight : styles.cellAlignLeft}`
            : column.type
            ? styles.cellAlignRight
            : styles.cellAlignLeft;
    };

    return (
        <>
            {tableColumns && tableRows?.length > 0 && (
                <TableContainer>
                    <Table className={styles.table}>
                        <TableHead className={styles.head}>
                            <TableRow className={styles.headRow}>
                                {tableColumns.map((column: Column) => (
                                    <TableCell key={column.prop} className={getCellClassName(column)}>
                                        <TableSortLabel
                                            active
                                            direction={column._direction ?? "desc"}
                                            onClick={() => sortRows(column.prop)}
                                            className={column.type ? styles.rightAlignSortLabel : ""}
                                        >
                                            <p className={`Label ${styles.headCellText}`}>
                                                {intl.formatMessage({ id: `common.${column.label}` })}
                                            </p>
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody className={styles.body}>
                            {tableRows
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row: any, index: number) => (
                                    <TableRow
                                        key={index}
                                        onClick={onRowClick && canClickRow(row) && (() => handleOnRowClick(row))}
                                        className={`${styles.bodyRow} ${onRowClick && canClickRow(row) ? styles.bodyClickable : ""}`}
                                        hover
                                    >
                                        {columns.map((column: Column) => (
                                            <TableCell key={`${index}${column.prop}`} className={getCellClassName(column)}>
                                                <p
                                                    style={{ textTransform: "none" }}
                                                    className={`Label ${styles.bodyCellText}`}
                                                >
                                                    {row[column.prop]}
                                                </p>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        className={`Label ${styles.pagination}`}
                        labelDisplayedRows={function defaultLabelDisplayedRows({ from, to, count }) {
                            return `${from}–${to} ${intl.formatMessage({ id: "common.of" })} ${
                                count !== -1 ? count : `more than ${to}`
                            }`;
                        }}
                        labelRowsPerPage={<></>}
                        rowsPerPageOptions={[10, 25, 50]}
                        component="div"
                        count={tableRows.length ? tableRows.length : numberOfRows}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={onChangePage ? onChangePage : handleChangePage}
                        onRowsPerPageChange={onChangeNumberOfRows ? onChangeNumberOfRows : handleChangeRowsPerPage}
                    />
                </TableContainer>
            )}
        </>
    );
};

export default CustomerPortalTable